/**
 * Client half of the HMAC-bound anonymous identity.
 *
 * The proof is minted server-side by the `anon-identity` edge function and
 * cached in localStorage next to the anon id it belongs to. It is sent as the
 * `x-anon-proof` header, alongside `x-anon-id` — the anon id is no longer put
 * in query strings (it leaked into logs and Referer headers).
 *
 * Failure is non-fatal: during the transition the backend still accepts a
 * request without a proof, so a mint failure degrades to legacy behaviour
 * instead of breaking favorites.
 */
import { getAnonUserId } from '@/lib/userIdentity';

const STORAGE_KEY = 'anonProof_v1';

type StoredProof = { id: string; proof: string };

let cached: StoredProof | null = null;
let inflight: Promise<StoredProof | null> | null = null;

function readStored(): StoredProof | null {
  if (cached) return cached;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredProof>;
    if (typeof parsed?.id === 'string' && typeof parsed?.proof === 'string') {
      cached = { id: parsed.id, proof: parsed.proof };
      return cached;
    }
  } catch { /* corrupt or unavailable storage */ }
  return null;
}

function writeStored(value: StoredProof): void {
  cached = value;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch { /* quota / private mode */ }
}

/** Test/reset hook — clears the in-memory cache. */
export function resetAnonProofCache(): void {
  cached = null;
  inflight = null;
}

async function mintProof(anonUserId: string): Promise<StoredProof | null> {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  if (!baseUrl || !apikey) return null;

  try {
    const res = await fetch(`${baseUrl}/functions/v1/anon-identity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey },
      body: JSON.stringify({ anon_user_id: anonUserId }),
    });
    if (!res.ok) return null;
    const parsed = await res.json().catch(() => null);
    const proof = parsed?.data?.proof;
    const id = parsed?.data?.anon_user_id;
    if (typeof proof !== 'string' || typeof id !== 'string') return null;
    const stored: StoredProof = { id, proof };
    writeStored(stored);
    return stored;
  } catch {
    return null;
  }
}

/** Cached proof for the current anon id, minting one on first use. */
export async function getAnonProof(): Promise<string | null> {
  const anonUserId = getAnonUserId();
  const stored = readStored();
  if (stored && stored.id === anonUserId) return stored.proof;

  if (!inflight) {
    inflight = mintProof(anonUserId).finally(() => { inflight = null; });
  }
  const result = await inflight;
  return result && result.id === anonUserId ? result.proof : null;
}

/** Headers carrying the anonymous identity. Always includes the id. */
export async function getAnonAuthHeaders(): Promise<Record<string, string>> {
  const anonUserId = getAnonUserId();
  const proof = await getAnonProof();
  return proof
    ? { 'x-anon-id': anonUserId, 'x-anon-proof': proof }
    : { 'x-anon-id': anonUserId };
}
